import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, CardComponent } from '@optimistic-tanuki/common-ui';
import { TextInputComponent } from '@optimistic-tanuki/form-ui';
import {
  Themeable,
  ThemeColors,
  ThemeService,
} from '@optimistic-tanuki/theme-lib';
import { LoginType } from '@optimistic-tanuki/ui-models';
import {
  OAuthButtonsComponent,
  OAuthProviderEvent,
} from '../oauth-buttons/oauth-buttons.component';
import { EmailAuthClientService } from '../../services/email-auth.service';

@Component({
  selector: 'lib-login-block',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardComponent,
    ButtonComponent,
    TextInputComponent,
    OAuthButtonsComponent,
  ],
  templateUrl: './login-block.component.html',
  styleUrl: './login-block.component.scss',
  host: {
    // Using standardized local variables with fallbacks
    '[style.--local-background]': 'background',
    '[style.--local-foreground]': 'foreground',
    '[style.--local-accent]': 'accent',
    '[style.--local-complement]': 'complement',
    '[style.--local-border-color]': 'borderColor',
    '[style.--local-transition-duration]': 'transitionDuration',
  },
})
export class LoginBlockComponent extends Themeable {
  @Input() title = 'login-block works!';
  @Input() description = 'login-block works!';
  @Input() heroSrc =
    'https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwzNjUyOXwwfDF8c2VhcmNofDJ8fGxvZ298ZW58MHx8fHwxNjg3NTY5NzA1&ixlib=rb-4.0.3&q=80&w=1080';
  @Input() heroAlt = 'login-block works!';
  // Hosts that render their own marketing column suppress the in-card hero so
  // the page does not end up with two headlines competing for the same job.
  @Input() showHero = true;
  @Input() errorMessage = '';
  @Input() pending = false;
  @Input() showOAuth = true;
  @Input() appId = '';
  @Input() ageGateEnabled = false;
  @Input() returnPath = '/';
  @Input() enabledOAuthProviders: string[] = [
    'google',
    'github',
    'microsoft',
    'facebook',
  ];
  @Output() submitEvent = new EventEmitter<LoginType>();
  @Output() oauthProviderSelected = new EventEmitter<OAuthProviderEvent>();
  loginForm: FormGroup;
  emailActionStatus = '';
  emailActionPending = false;
  constructor(
    private readonly fb: FormBuilder,
    private readonly emailAuth: EmailAuthClientService
  ) {
    super();
    this.loginForm = this.fb.group({
      email: this.fb.control(''),
      password: this.fb.control(''),
      dateOfBirth: this.fb.control(''),
      guardianEmail: this.fb.control(''),
    });
  }

  override applyTheme(colors: ThemeColors): void {
    // Use standardized color assignments with design tokens
    this.background = `linear-gradient(30deg, ${colors.accent}, ${colors.background})`;
    this.accent = colors.accent;
    this.borderColor = colors.complementary;

    // Use standardized gradient names
    if (this.theme === 'dark') {
      this.borderGradient = colors.complementaryGradients['dark'];
    } else {
      this.borderGradient = colors.complementaryGradients['light'];
    }

    this.foreground = colors.foreground;
    this.complement = colors.complementary;
    this.transitionDuration = '0.15s'; // Use standardized duration
    this.borderColor = colors.complementary;
  }

  onFormChange(_e: string) {}

  onSubmit() {
    const { email, password, dateOfBirth, guardianEmail } =
      this.loginForm.value;
    this.submitEvent.emit({
      email,
      password,
      ...(dateOfBirth ? { dateOfBirth } : {}),
      ...(guardianEmail ? { guardianEmail } : {}),
    });
  }

  get showAgeEligibilityFields(): boolean {
    return (
      this.ageGateEnabled &&
      /AGE_CHECK_REQUIRED|AGE_UNDER_13|GUARDIAN_EMAIL_REQUIRED/.test(
        this.errorMessage
      )
    );
  }

  get showGuardianEmailField(): boolean {
    return (
      this.ageGateEnabled && /GUARDIAN_EMAIL_REQUIRED/.test(this.errorMessage)
    );
  }

  onOAuthProvider(event: OAuthProviderEvent) {
    this.oauthProviderSelected.emit({
      ...event,
      ...(this.ageGateEnabled
        ? { dateOfBirth: this.loginForm.value.dateOfBirth || undefined }
        : {}),
    });
  }

  requestMagicLink() {
    this.requestEmailAction('magic-link');
  }

  requestPasswordReset() {
    this.requestEmailAction('password-reset');
  }

  requestVerification() {
    this.requestEmailAction('verification');
  }

  private requestEmailAction(
    purpose: 'verification' | 'magic-link' | 'password-reset'
  ) {
    const email = String(this.loginForm.value.email || '').trim();
    if (!this.appId) {
      this.emailActionStatus = 'Email sign-in is not available right now.';
      return;
    }
    if (!email) {
      this.emailActionStatus = 'Enter your email address first.';
      return;
    }
    if (this.emailActionPending) {
      this.emailActionStatus = 'Email request already in progress.';
      return;
    }
    this.emailActionPending = true;
    this.emailActionStatus = '';
    this.emailAuth
      .request(this.appId, email, purpose, this.returnPath)
      .subscribe({
        next: () => {
          this.emailActionPending = false;
          this.emailActionStatus =
            'If that account exists, a secure email is on its way.';
        },
        error: () => {
          this.emailActionPending = false;
          this.emailActionStatus =
            'Email could not be requested right now. Please try again.';
        },
      });
  }
}
